import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, Scale, Ruler, Clock, Heart, Utensils, ExternalLink, Brain, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { getSpeciesById, getQuestionsForSpecies, getFactsForSpecies } from '@/data';
import { conservationLabels } from '@/types';
import { useProgress } from '@/hooks/useProgress';

export function SpeciesDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { markSpeciesViewed, hasViewedSpecies, getQuestionProgress } = useProgress();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const species = getSpeciesById(id || '');
  const relatedQuestions = getQuestionsForSpecies(id || '');
  const relatedFacts = getFactsForSpecies(id || '');

  // Reset image index when species changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [id]);

  const images = species?.images || [];
  const hasMultipleImages = images.length > 1;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Mark species as viewed when page loads
  useEffect(() => {
    if (species && !hasViewedSpecies(species.id)) {
      markSpeciesViewed(species.id);
    }
  }, [species, markSpeciesViewed, hasViewedSpecies]);

  if (!species) {
    return (
      <div className="max-w-lg mx-auto px-4 py-6 text-center">
        <p className="text-slate-500">Species not found</p>
        <Link to="/species" className="text-otter-600 mt-2 inline-block">
          Back to Species
        </Link>
      </div>
    );
  }

  const correctAnswers = relatedQuestions.filter(
    (q) => getQuestionProgress(q.id)?.lastCorrect
  ).length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-lg mx-auto pb-6"
    >
      {/* Hero Image Gallery */}
      <div className="relative h-72 bg-gradient-to-br from-otter-200 to-water-200 dark:from-otter-900 dark:to-water-900 overflow-hidden">
        {/* Image Display */}
        <AnimatePresence mode="wait">
          {images.length > 0 ? (
            <motion.img
              key={currentImageIndex}
              src={images[currentImageIndex].url}
              alt={images[currentImageIndex].alt || species.commonName}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-8xl">
              🦦
            </div>
          )}
        </AnimatePresence>

        {/* Image Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}

        {/* Image Dots Indicator */}
        {hasMultipleImages && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex
                    ? 'bg-white w-4'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Conservation Badge */}
        <div className="absolute top-4 right-4">
          <Badge
            variant="conservation"
            conservationStatus={species.conservationStatus}
            className="text-sm px-3 py-1"
          >
            {conservationLabels[species.conservationStatus]}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-8 relative z-10 space-y-4">
        {/* Title Card */}
        <Card>
          <h1 className="text-2xl font-display font-bold text-slate-800 dark:text-white">
            {species.commonName}
          </h1>
          <p className="text-lg text-slate-500 italic mb-3">
            {species.scientificName}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Genus: <span className="font-medium">{species.genus}</span>
          </p>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="text-center">
            <Ruler className="w-5 h-5 mx-auto mb-1 text-otter-500" />
            <p className="text-lg font-bold text-slate-800 dark:text-white">
              {species.characteristics.size.min}-{species.characteristics.size.max}
            </p>
            <p className="text-xs text-slate-500">{species.characteristics.size.unit} length</p>
          </Card>
          <Card className="text-center">
            <Scale className="w-5 h-5 mx-auto mb-1 text-otter-500" />
            <p className="text-lg font-bold text-slate-800 dark:text-white">
              {species.characteristics.weight.min}-{species.characteristics.weight.max}
            </p>
            <p className="text-xs text-slate-500">{species.characteristics.weight.unit}</p>
          </Card>
          <Card className="text-center">
            <Clock className="w-5 h-5 mx-auto mb-1 text-otter-500" />
            <p className="text-lg font-bold text-slate-800 dark:text-white">
              {species.characteristics.lifespan.wild}
            </p>
            <p className="text-xs text-slate-500">years (wild)</p>
          </Card>
          <Card className="text-center">
            <MapPin className="w-5 h-5 mx-auto mb-1 text-otter-500" />
            <p className="text-lg font-bold text-slate-800 dark:text-white capitalize">
              {species.habitat.waterType === 'both' ? 'Both' : species.habitat.waterType}
            </p>
            <p className="text-xs text-slate-500">water type</p>
          </Card>
        </div>

        {/* Habitat */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-green-600" />
            <h2 className="font-display font-bold text-slate-800 dark:text-white">
              Habitat & Range
            </h2>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Regions</p>
              <div className="flex flex-wrap gap-1">
                {species.habitat.regions.map((region) => (
                  <span
                    key={region}
                    className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs"
                  >
                    {region}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Countries</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {species.habitat.countries.join(', ')}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Environment</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {species.habitat.environment.join(', ')}
              </p>
            </div>
          </div>
        </Card>

        {/* Diet */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Utensils className="w-5 h-5 text-orange-600" />
            <h2 className="font-display font-bold text-slate-800 dark:text-white">
              Diet
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {species.diet.map((food) => (
              <span
                key={food}
                className="px-2 py-1 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded-full text-sm"
              >
                {food}
              </span>
            ))}
          </div>
        </Card>

        {/* Behavior */}
        <Card>
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-pink-600" />
            <h2 className="font-display font-bold text-slate-800 dark:text-white">
              Behavior
            </h2>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
            {species.behavior.social}
          </p>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Unique Traits</p>
            <ul className="space-y-1">
              {species.behavior.uniqueTraits.map((trait, i) => (
                <li key={i} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                  <span className="text-otter-500">•</span>
                  {trait}
                </li>
              ))}
            </ul>
          </div>
        </Card>

        {/* Distinguishing Features */}
        <Card>
          <h2 className="font-display font-bold text-slate-800 dark:text-white mb-3">
            Distinguishing Features
          </h2>
          <ul className="space-y-1">
            {species.characteristics.distinguishingFeatures.map((feature, i) => (
              <li key={i} className="text-sm text-slate-600 dark:text-slate-400 flex items-start gap-2">
                <span className="text-otter-500">✦</span>
                {feature}
              </li>
            ))}
          </ul>
        </Card>

        {/* Fun Facts */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-200 dark:border-amber-700">
          <h2 className="font-display font-bold text-slate-800 dark:text-white mb-3">
            🎉 Fun Facts
          </h2>
          <ul className="space-y-3">
            {species.funFacts.map((fact, i) => (
              <li key={i} className="text-sm text-slate-700 dark:text-slate-300">
                {fact}
              </li>
            ))}
          </ul>
        </Card>

        {/* Quiz Progress */}
        {relatedQuestions.length > 0 && (
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                <h2 className="font-display font-bold text-slate-800 dark:text-white">
                  Quiz Progress
                </h2>
              </div>
              <span className="text-sm text-slate-500">
                {correctAnswers}/{relatedQuestions.length} correct
              </span>
            </div>
            <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ width: `${(correctAnswers / relatedQuestions.length) * 100}%` }}
              />
            </div>
            <Link to={`/quiz?species=${species.id}`} className="block mt-3">
              <Button variant="secondary" className="w-full">
                Take {species.commonName} Quiz
              </Button>
            </Link>
          </Card>
        )}

        {/* Source & Image Credits */}
        <div className="text-center text-xs text-slate-400 dark:text-slate-500 pt-4 space-y-2">
          <a
            href={species.source}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:text-otter-500"
          >
            Source <ExternalLink className="w-3 h-3" />
          </a>
          {images.length > 0 && (
            <p>
              Photo: {images[currentImageIndex].credit}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
